import { isPlatformSuperAdmin } from 'pure-js/libs/isSuperUser'
import { OrgRole } from 'pure-js/types/OrganizationMembership'
import { State } from '../hooks/useAppState'
import { hasCompletedIntroduction, hasCompleteUserInfo } from '../libs/SignedInMachine'
import { canAccessRoute } from '../utils/routeProtection'
import * as processHandlers from '../apiLayer/processHandlers/organizations'
import {
  getActiveOrgAdminMembership,
  getOnboardingRedirectPathIfPending,
  isNonOperationalOrganizationStatus,
  shouldHonorIntendedDestination
} from '../features/organization/orgOnboardingNavigation'
import { getActiveOrganizationId } from '../features/organization/orgStateHelpers'

export const onLoginToIntraction = async (
  state: State,
  {
    navigate,
    window: _window,
    locationState
  }: { navigate: (path: string) => void; window: Window; locationState?: { from?: { pathname?: string } } }
) => {
  const { user } = state

  // Check if user needs to complete profile (displayName, email, or privacy notice acknowledgement)
  if (!hasCompleteUserInfo(user)) {
    return navigate('/signedin')
  }

  // Derive active org role inline
  let activeOrgId = getActiveOrganizationId(user)
  if (activeOrgId) {
    try {
      const activeOrgResponse = await processHandlers.getOrganization(state, {
        organizationId: activeOrgId
      })
      const activeStatus = activeOrgResponse?.data?.status
      if (isNonOperationalOrganizationStatus(activeStatus)) {
        const fallbackOrgId = user.platformDefaultOrganizationId
        const hasFallback = fallbackOrgId && user.organizations?.some((m) => m.organizationId === fallbackOrgId)
        if (hasFallback && fallbackOrgId !== activeOrgId) {
          await processHandlers.setActiveOrganization(state, { organizationId: fallbackOrgId })
          activeOrgId = fallbackOrgId
        }
      }
    } catch {
      // Non-fatal — continue with current active org.
    }
  }

  const activeMembership = user.organizations?.find((m) => m.organizationId === activeOrgId) ?? user.organizations?.[0]
  const activeOrgRole: OrgRole = isPlatformSuperAdmin(user) ? OrgRole.ADMIN : (activeMembership?.role ?? OrgRole.GAMER)

  let pendingOnboardingRedirectPath: string | null = null
  const adminMembership = getActiveOrgAdminMembership(user)
  if (adminMembership) {
    try {
      const orgResponse = await processHandlers.getOrganization(state, {
        organizationId: adminMembership.organizationId
      })
      pendingOnboardingRedirectPath = getOnboardingRedirectPathIfPending(
        user,
        orgResponse?.data?.status,
        adminMembership.organizationId
      )
    } catch {
      // Non-fatal — fall through to role-based routing.
    }
  }

  if (pendingOnboardingRedirectPath) {
    return navigate(pendingOnboardingRedirectPath)
  }

  // Check if there's a saved location to redirect to (from ProtectedRoute)
  const intendedDestination = locationState?.from?.pathname
  if (
    shouldHonorIntendedDestination(intendedDestination, {
      canAccess: (path) => canAccessRoute(path, activeOrgRole),
      pendingOnboardingRedirectPath
    })
  ) {
    return navigate(intendedDestination!)
  }

  // Redirect based on active org role
  if (activeOrgRole === OrgRole.FACILITATOR || activeOrgRole === OrgRole.ADMIN) {
    return navigate('/facilitator')
  }

  // Gamer role
  if (!hasCompletedIntroduction(user)) {
    return navigate('/gamer/onboarding')
  }
  return navigate('/gamer')
}
